import React, { useState } from 'react';
import ButtonWithTooltip from 'components/button-with-tooltip/ButtonWithTooltip';
import COFCardDisplay from '../../invoices/card-on-file/COFCardDisplay';
import CustomerAddCOFModal from './CustomerAddCOFModal';
import { useDeleteCustomerCardMutation } from 'containers/customers/api';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { useAppSelector } from 'hooks';
import { AddressField } from '../../invoices/invoices.model';
import { CustomerCard } from '../customers.model';
import DeleteCOFModal from './DeleteCOFModal';
import { logAnalyticEvent } from 'utils/analytics';
import { CleverTapEventsCardOnFile } from 'containers/invoices/card-on-file/events';
import ManageCardModal from 'containers/invoices/card-on-file/ManageCardModal';

interface CustomerCOFWidgetProps {
  customerCardsDataResponse: {
    data?: CustomerCard[];
    isLoading: boolean;
    error?: unknown;
  };
  isEditable?: boolean;
  canCreateCards?: boolean;
  customerCardObject: {
    name: string;
    email: string;
    customerId: string;
    billingAddress?: AddressField;
  };
}

const CustomerCOFWidget: React.FC<CustomerCOFWidgetProps> = ({
  customerCardsDataResponse,
  isEditable = false,
  canCreateCards = false,
  customerCardObject
}) => {
  const currentTeam = useAppSelector(selectCurrentTeam);

  const [isAddCOFModalOpen, setIsAddCOFModalOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCardData, setSelectedCardData] = useState<CustomerCard | null>(null);
  const [isManageCardModalOpen, setIsManageCardModalOpen] = useState(false);

  const [deleteCustomerCard, { isLoading: isDeleting }] = useDeleteCustomerCardMutation();
  const { data: customerCardsDataObj, isLoading } = customerCardsDataResponse;
  const customerCardsData = customerCardsDataObj || [];

  const handleAddCard = () => {
    setSelectedCardData(null);
    setIsAddCOFModalOpen(true);

    logAnalyticEvent(CleverTapEventsCardOnFile.webCOFAddCardClicked, {
      'Customer Id': customerCardObject?.customerId,
      Source: 'Customer Details'
    });
  };

  const handleCloseAddCOFModal = () => {
    setIsAddCOFModalOpen(false);
    setSelectedCardData(null);
  };

  const handleEditCard = (card: CustomerCard) => {
    setSelectedCardData(card);
    setIsAddCOFModalOpen(true);
  };

  const handleDeleteCard = (card: CustomerCard) => {
    if (!card) {
      return;
    }

    setSelectedCardData(card);
    setShowDeleteModal(true);

    logAnalyticEvent(CleverTapEventsCardOnFile.webCOFDeleteCardClicked, {
      'Customer Id': card.customerId,
      'Card Network': card.network,
      'Card Last 4': card.cardLast4,
      Source: 'Customer COF'
    });
  };

  const handleManageCardClick = (card: CustomerCard) => {
    setSelectedCardData(card);
    setIsManageCardModalOpen(true);
  };

  const handleCloseManageCardModal = () => {
    setIsManageCardModalOpen(false);
    setSelectedCardData(null);
  };

  const handleConfirmDelete = async () => {
    if (currentTeam?.id && selectedCardData) {
      try {
        await deleteCustomerCard({
          teamId: currentTeam.id,
          customerId: selectedCardData.customerId,
          cardId: selectedCardData.id
        }).unwrap();

        logAnalyticEvent(CleverTapEventsCardOnFile.webCOFDeleteCardSuccess, {
          'Customer Id': selectedCardData?.customerId,
          'Card Network': selectedCardData?.network,
          'Card Last 4': selectedCardData?.cardLast4,
          Source: 'Customer COF'
        });
      } catch (error) {
        logAnalyticEvent(CleverTapEventsCardOnFile.webCOFDeleteCardFailed, {
          'Customer Id': selectedCardData?.customerId,
          'Card Network': selectedCardData?.network,
          'Card Last 4': selectedCardData?.cardLast4,
          Error: error?.message || 'Unknown error',
          Source: 'Customer COF'
        });
      } finally {
        setShowDeleteModal(false);
        setIsManageCardModalOpen(false);
        setSelectedCardData(null);
      }
    }
  };

  const handleCancelDelete = () => {
    if (selectedCardData) {
      logAnalyticEvent(CleverTapEventsCardOnFile.webCOFDeleteCardCancelled, {
        'Customer Id': selectedCardData?.customerId,
        'Card Network': selectedCardData?.network,
        'Card Last 4': selectedCardData?.cardLast4,
        Source: 'Customer Details'
      });
    }

    setShowDeleteModal(false);
  };

  const getCardDataForModal = () => {
    if (selectedCardData) {
      // When editing, map CustomerCard to expected format
      const billingAddress = {
        id: 0,
        street: selectedCardData.address.line1,
        apartment: selectedCardData.address.line2 || '',
        city: selectedCardData.address.city,
        state: selectedCardData.address.state,
        zipCode: selectedCardData.address.zipCode,
        country: selectedCardData.address.country,
        addressNote: '',
        addressName: ''
      };
      return {
        name: selectedCardData.cardHolderName,
        email: selectedCardData.email,
        customerId: selectedCardData.customerId,
        billingAddress: selectedCardData.address ? { ...billingAddress } : undefined,
        id: selectedCardData.id
      };
    }
    // When adding, use original customerCardObject
    return { ...customerCardObject, id: undefined };
  };

  const hasCards = customerCardsData?.length > 0;

  return (
    <div className="mx-4 rounded-md border border-border">
      <div className="flex justify-between px-4 py-3 text-sbase leading-[18px] tracking-[-0.3px]">
        <div className="font-bold text-primaryText">Card on file</div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center p-4">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
        </div>
      ) : (
        hasCards && (
          <div className="px-4 py-3">
            <div className="space-y-3">
              {customerCardsData?.map(card => (
                <div
                  key={card.id}
                  className="flex cursor-pointer items-center justify-between transition-colors hover:bg-gray-50"
                  onClick={() => handleManageCardClick(card)}>
                  <COFCardDisplay
                    cardData={card}
                    isEditable={isEditable}
                    onManageCardClick={handleManageCardClick}
                    showChevronIcon={true}
                  />
                </div>
              ))}
            </div>
          </div>
        )
      )}

      {!isLoading && !hasCards && (
        <div className="p-4">
          <ButtonWithTooltip
            id="customer-details-add-card"
            className="h-10 w-full"
            childrenContainerStyles="text-17px font-semibold"
            onClick={handleAddCard}
            disabled={!canCreateCards}
            tooltipPosition="custom"
            customTooltipClassName="-translate-x-[73%] left-0 -top-[44px] before:left-[73%] before:top-full 
            border-border"
            tooltipText="You don't have permission to add card on file">
            Add card on file
          </ButtonWithTooltip>
        </div>
      )}

      {showDeleteModal && (
        <DeleteCOFModal
          onPrimaryBtnClick={handleConfirmDelete}
          onSecondaryBtnClick={handleCancelDelete}
          onCloseIconClick={handleCancelDelete}
          isActionInProgress={isDeleting}
        />
      )}
      <CustomerAddCOFModal
        isOpen={isAddCOFModalOpen}
        onClose={handleCloseAddCOFModal}
        cardData={getCardDataForModal()}
        successCallback={handleCloseAddCOFModal}
      />
      {isManageCardModalOpen && selectedCardData && (
        <ManageCardModal
          open={isManageCardModalOpen}
          cardData={selectedCardData}
          onClose={handleCloseManageCardModal}
          onDeleteCard={handleDeleteCard}
          isEditable={false}
        />
      )}
    </div>
  );
};

export default CustomerCOFWidget;
